"use client"

import { useMemo, useState, type ReactNode } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import {
  ENERGY_COST_DEFAULTS,
  ENERGY_COST_QUESTION,
  calculateEnergyCost,
  energyCostAnswer,
} from "@/lib/tools/energy-cost"
import { EmbedThisCard } from "@/components/data/embed-this-card"

// ---------- Page ----------

export function EnergyCostCalculatorPageClient({ children }: { children?: ReactNode }) {
  const [kwh, setKwh] = useState<string>(ENERGY_COST_DEFAULTS.kwhPerYear)
  const [rate, setRate] = useState<string>(ENERGY_COST_DEFAULTS.rateCentsPerKwh)

  // The arithmetic lives in lib/tools/energy-cost.ts (FRIDGE-356) so the
  // /embed/energy-cost-calculator widget and this page can never disagree; the
  // logic there is this page's, moved verbatim.
  const results = useMemo(() => calculateEnergyCost(kwh, rate), [kwh, rate])

  // Explicit scenarios at the entered rate, not measured market averages.
  const comparison = [
    { label: "You", cost: results.yearly, fill: "#ff9951" },
    { label: "500 kWh/yr example", cost: calculateEnergyCost(500, rate).yearly, fill: "#0469f0" },
    { label: "800 kWh/yr example", cost: calculateEnergyCost(800, rate).yearly, fill: "#9ca3af" },
  ]
  const maxCost = Math.max(1, ...comparison.map((c) => c.cost))

  // Product carousel is driven by ToolProductCarousel which fetches real
  // data from Supabase via getTrendingInCollection.

  return (
    <ToolPageShell
      toolName="Energy Cost Calculator"
      heroEyebrow="Energy & Cost"
      heroTitlePre="How much does your fridge"
      heroTitleItalic="actually"
      heroTitlePost="cost to run?"
      heroSubtitle={energyCostAnswer(kwh, rate) ?? "Enter annual kWh from your EnergyGuide label and your electricity rate to estimate annual running cost."}
      heroMeta={["⏱ ~30 sec", "EnergyGuide input × your electricity rate"]}
      heroShare={<ToolShareBar title="Energy Cost Calculator" />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: `$${results.yearly.toFixed(2)}`, label: "Estimated electricity / yr", note: "From the inputs below" },
            { value: "Free", label: "Signup", note: "Forever" },
            { value: "2", label: "Inputs", note: "Annual kWh and rate" },
            { value: kwh || "—", label: "Annual kWh input", note: "Replace with your label" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={[
        // FRIDGE-902: the estimate sentence under its question, first and open,
        // so the FAQPage node the server page emits (AnswerFaqJsonLd) describes
        // a visible pair. The visitor's own inputs update it with the hero.
        {
          question: ENERGY_COST_QUESTION,
          answer: energyCostAnswer(kwh, rate) ?? energyCostAnswer(ENERGY_COST_DEFAULTS.kwhPerYear, ENERGY_COST_DEFAULTS.rateCentsPerKwh)!,
        },
        {
          question: "Where do I find my fridge's annual kWh?",
          answer:
            "On the yellow EnergyGuide sticker that came attached to the unit, or in the spec sheet on the manufacturer's website. Look for 'Estimated Yearly Electricity Use'.",
        },
        {
          question: "Why is my electricity rate different from my neighbor's?",
          answer:
            "Rates vary by utility, plan (fixed / variable / tiered), and season. For the most honest number, divide your most recent bill's total dollars by its total kilowatt-hours.",
        },
        {
          question: "Does this include water or ice maker costs?",
          answer:
            "This estimates electricity only from the annual kWh you enter. It does not separately estimate water charges, and actual ice-maker use can change electricity consumption.",
        },
        {
          question: "What do the comparison examples mean?",
          answer:
            "The 500 and 800 kWh examples use your entered rate. They illustrate the arithmetic; neither represents an observed average, an appliance age group or a specific product.",
        },
        {
          question: "Can I model solar or off-peak rates?",
          answer:
            "Not yet — but it's on the roadmap. For now, use your off-peak cents/kWh to get a lower-bound estimate.",
        },
        {
          question: "Is my data saved?",
          answer:
            "No. Nothing you type is stored, logged, or sent anywhere. Refresh the page and it's gone.",
        },
      ]}
      relatedTools={[
        { title: "Rebate Finder", category: "Energy & Cost", description: "Every utility, state, and federal rebate in your ZIP.", href: "/tools/refrigerator-rebate-finder", time: "~20 sec" },
        { title: "Refrigerator Size Calculator", category: "Size & Capacity", description: "The right cubic-foot range for your household.", href: "/tools/refrigerator-size-calculator", time: "~45 sec" },
        { title: "Lifespan Estimator", category: "Specialty", description: "How many years your fridge has left.", href: "/tools/refrigerator-lifespan-calculator", time: "~45 sec" },
        { title: "Temperature Converter", category: "Temperature", description: "°F ↔ °C for every storage zone.", href: "/tools/cooking-temperature-converter", time: "~5 sec" },
      ]}
      cta={{
        title: "Compare refrigerator models and energy labels",
        body: "Check model-specific annual kWh and prices before comparing running costs. Energy savings depend on your existing appliance and electricity rate.",
        buttonLabel: "Browse refrigerators",
        buttonHref: "/collections/energy-star-appliances",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            {/* Input card — bordered white block, not sticky */}
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">
                  STEP 01
                </span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Your fridge,
                <br />
                your rate.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Find kWh/yr on the yellow EnergyGuide label. Find your rate on last month&apos;s bill.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label htmlFor="energy-cost-annual-kwh" className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Annual kWh usage
                  </label>
                  <div className="relative">
                    <input
                      id="energy-cost-annual-kwh"
                      type="number"
                      value={kwh}
                      onChange={(e) => setKwh(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">
                      kWh/yr
                    </span>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Use your model’s annual EnergyGuide kWh
                  </div>
                </div>
                <div>
                  <label htmlFor="energy-cost-electricity-rate" className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Electricity rate
                  </label>
                  <div className="relative">
                    <input
                      id="energy-cost-electricity-rate"
                      type="number"
                      step="0.01"
                      value={rate}
                      onChange={(e) => setRate(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">
                      ¢/kWh
                    </span>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Enter your bill’s electricity price in cents per kWh
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            {/* Result panel — headline result only */}
            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{
                  background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)",
                }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{
                    background: "rgba(255,153,81,0.35)",
                    filter: "blur(60px)",
                    transform: "translate(25%, -35%)",
                  }}
                />
                <div className="relative">
                  <div className="flex items-center justify-between mb-8 flex-wrap gap-2">
                    <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70">
                      Your annual cost
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-white/60">
                      <RefreshCw className="w-3 h-3" /> Updated live
                    </div>
                  </div>
                  <div
                    className="font-black"
                    style={{
                      fontSize: "clamp(36px, 8vw, 88px)",
                      letterSpacing: "-0.045em",
                      lineHeight: 0.9,
                    }}
                  >
                    {`$${results.yearly.toLocaleString("en-US", { maximumFractionDigits: 0 })}`}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">
                      /yr
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-6 md:mt-8">
                    {[
                      { l: "Per day", v: `$${results.daily.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
                      { l: "Per month", v: `$${results.monthly.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
                      { l: "Per decade", v: `$${results.decade.toLocaleString("en-US", { maximumFractionDigits: 0 })}` },
                    ].map((s) => (
                      <div
                        key={s.l}
                        className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15"
                      >
                        <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                          {s.l}
                        </div>
                        <div className="text-[18px] sm:text-[22px] font-black tracking-tight">
                          {s.v}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Comparison chart — full width below the 50/50 grid */}
          <div className="bg-white rounded-xl p-5 md:p-6 border border-gray-200">
            <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
              <h3 className="text-[16px] font-black tracking-[-0.02em] text-gray-900">
                Compare example electricity use
              </h3>
              <span className="text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold">
                Annual cost in dollars
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
              {comparison.map((c) => (
                <div key={c.label}>
                  <div className="flex justify-between text-[12px] mb-1">
                    <span className="font-semibold">{c.label}</span>
                    <span className="font-mono font-bold" style={{ color: c.fill }}>
                      {`$${c.cost.toLocaleString("en-US", { maximumFractionDigits: 0 })}`}
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{ width: `${(c.cost / maxCost) * 100}%`, background: c.fill }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-gray-100 text-[13px] text-gray-600 leading-[1.5]">
              Both examples use the electricity rate you entered. They are arithmetic scenarios,
              not national averages or recommendations to replace an appliance. Actual costs
              depend on usage, conditions and electricity charges.
            </div>
          </div>
        </div>
      </section>

      {/* ============ SHOPPABLE RECOMMENDATIONS (real DB products) ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Shop smarter"
          title="Compare refrigerator energy labels"
          description="Check each model’s annual kWh and compare it using your own electricity rate."
          collectionType="french-door"
          fallbackCollectionType="top-freezer"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{
                  fontSize: "clamp(24px, 3vw, 36px)",
                  letterSpacing: "-0.03em",
                  lineHeight: 1.05,
                }}
              >
                The math is <em className="italic text-[#ff9951]">simple.</em> The data isn&apos;t.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                This calculator multiplies the annual energy use you enter by your electricity
                price. Default inputs are examples and do not establish a national average.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "You enter your rate + kWh",
                  d: "Use your bill and your model’s EnergyGuide label; actual use and charges can differ from the estimate.",
                },
                {
                  n: "02",
                  t: "We multiply and divide",
                  d: "(kWh × rate ÷ 100) for annual; ÷365 for daily; ÷12 for monthly. That's it.",
                },
                {
                  n: "03",
                  t: "Compare explicit scenarios",
                  d: "The 500 and 800 kWh examples use the same entered rate, so you can compare electricity use without an unsupported market baseline.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ EMBED THIS CALCULATOR (FRIDGE-356) ============ */}
      <section className="bg-white py-8 md:py-10">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <EmbedThisCard asset="energy-cost-calculator" />
        </div>
      </section>

      {/* ============ SECOND CAROUSEL — different collection ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Top-freezer fridges with the lowest annual cost"
          description="The cheapest style to run, year over year."
          collectionType="top-freezer"
          fallbackCollectionType="bottom-freezer"
        />
      </section>
      {/* Server-rendered supporting data belongs inside the shell, before its footer. */}
      {children}
    </ToolPageShell>
  )
}
